import clsx from 'clsx'

import { RecentActivityMenu } from './RecentActivityMenu'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { Tag, type TagColor } from '@/components/tag/Tag'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { useIsTruncated } from '@/components/ui/tooltip/useIsTruncated'
import { DraftChip } from '@/drafting/components/DraftChip'
import type { ConversationDraft } from '@/drafting/store/selectors'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'

export const RecentActivityTypes = {
  AI_ASSISTANT: 'AI Assistant',
  COMBINED_RESEARCH: 'Combined Research',
  CASE_LAW: 'Case Law',
  LAWS_AND_REGULATIONS: 'Laws & Regulations',
} as const

export type RecentActivityType = (typeof RecentActivityTypes)[keyof typeof RecentActivityTypes]

const typeToTagColor: Record<RecentActivityType, TagColor> = {
  [RecentActivityTypes.AI_ASSISTANT]: 'Blue',
  [RecentActivityTypes.COMBINED_RESEARCH]: 'Green',
  [RecentActivityTypes.CASE_LAW]: 'Yellow',
  [RecentActivityTypes.LAWS_AND_REGULATIONS]: 'Black',
}

export interface RecentActivityCardProps {
  /** The title of the activity */
  title?: string
  /** The description of the activity (this will be truncated at one line) */
  description?: string
  /** The date of the activity */
  date?: string
  /** Optional type badge (AI Assistant, Combined Research, Case Law, Laws & Regulations) */
  type?: RecentActivityType
  /** Called when the card is clicked. Not fired when clicking the overflow menu. */
  onClick?: () => void
  /** Called when Delete is selected. If provided with other callbacks, shows the menu. */
  onDelete?: () => void
  /** Called when Rename is selected. If provided with other callbacks, shows the menu. */
  onRename?: () => void
  /** Called when Download is selected. If provided with other callbacks, shows the menu. */
  onDownload?: () => void
  /** When provided, shows a "Move to matter" submenu. */
  onMoveToMatter?: (matterId: string, matterName?: string) => void
  /** Matter id for this item. */
  matterId?: string | null
  /** Drafts produced by this activity, empty/undefined renders nothing. */
  drafts?: ConversationDraft[]
  /** Called with a draft's artifact version id when its chip is clicked. */
  onDraftClick?: (artifactVersionId: string) => void
  /** When true, shows skeleton placeholders instead of content. */
  loading?: boolean
  /** When true, shows a "Read Only" tag on the card. */
  readonly?: boolean
  /** Who the activity belongs to, shown in the metadata row (e.g. "Me" or another person's name). */
  owner?: string
  /** Force the owner's truncation hover label open. Storybook/testing only. */
  forceOwnerTooltipOpen?: boolean
}

/**
 * RecentActivityCard
 *
 * A list item displaying title, description, and date.
 * On hover, shows a menu icon in the top-right when any of action props are provided.
 */
export function RecentActivityCard({
  date = '',
  description,
  drafts,
  forceOwnerTooltipOpen = false,
  loading = false,
  matterId = null,
  onClick,
  onDelete,
  onDownload,
  onDraftClick,
  onMoveToMatter,
  onRename,
  owner,
  readonly = false,
  title = '',
  type,
}: RecentActivityCardProps) {
  const showMenu = !!(onDelete || onRename || onDownload || onMoveToMatter)
  const { ref: ownerRef, isTruncated: isOwnerTruncated } = useIsTruncated<HTMLSpanElement>()
  // On touch-only devices the tooltip toggles on tap and swallows it; skip it and let the name truncate.
  const isTouchOnly = useIsTouchOnlyDevice()

  if (loading) {
    return (
      <div
        className="flex min-w-0 w-full flex-col items-start gap-1.5 border-b border-brand-neutral-200 pl-4 pr-4 py-4"
        aria-busy
        aria-label="Loading activity"
      >
        <LoadingSkeleton width="50%" lineHeight="1.25rem" />
        <LoadingSkeleton width="100%" lineHeight="1.25rem" />
        <LoadingSkeleton width="15%" lineHeight="1rem" />
      </div>
    )
  }

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      className={clsx(
        'group/card relative flex min-w-0 w-full flex-col items-start gap-1.5 border-b border-brand-neutral-200 pl-4 pr-4 py-4',
        'hover-capable:hover:bg-brand-neutral-100',
        onClick && 'cursor-pointer'
      )}
    >
      <div className="relative min-w-0 w-full">
        <h3 className={clsx('min-w-0 truncate font-rethink-sans text-sm font-semibold leading-5 text-brand-neutral-950', showMenu ? 'pr-10' : 'pr-0')}>
          {title}
        </h3>
        {showMenu && <RecentActivityMenu onDelete={onDelete} onDownload={onDownload} onMoveToMatter={onMoveToMatter} onRename={onRename} matterId={matterId} />}
      </div>

      {description && <p className="min-w-0 max-w-full truncate text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-800">{description}</p>}

      <div className="flex w-full min-w-0 items-center gap-3">
        <div className="flex min-w-0 items-center gap-1 text-xs font-medium leading-4 text-brand-neutral-800">
          <span className="shrink-0 whitespace-nowrap">{date}</span>
          {owner && (
            <>
              <span className="shrink-0" aria-hidden="true">
                •
              </span>
              <Tooltip
                title={!isTouchOnly && (isOwnerTruncated || forceOwnerTooltipOpen) ? owner : ''}
                placement="top-start"
                offsetY={-12}
                forceOpen={forceOwnerTooltipOpen}
                disableInteractive
              >
                <span ref={ownerRef} className="min-w-0 truncate">
                  {owner}
                </span>
              </Tooltip>
            </>
          )}
        </div>
        {(readonly || type) && (
          <div className="flex shrink-0 items-center gap-1">
            {readonly && (
              <Tag variant="tonal" color="Black" size="2xs">
                View Only
              </Tag>
            )}
            {type && (
              <Tag variant="tonal" color={typeToTagColor[type]} size="2xs">
                {type}
              </Tag>
            )}
          </div>
        )}
      </div>

      {drafts && drafts.length > 0 && (
        <div className="flex w-full flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {drafts.map((draft) => (
            <DraftChip key={draft.artifactVersionId} name={draft.name} onClick={() => onDraftClick?.(draft.artifactVersionId)} />
          ))}
        </div>
      )}
    </div>
  )
}
